// Addresses: / (leads), /tasks, /stats, /settings, /contact/<contact id> (a lead opened over the board).
// No router library: the path is parsed into a Route, and moving around pushes history entries, so the phone's back
// button and the browser's back/forward behave, and any screen can be linked to or reloaded.
import { useCallback, useEffect, useState } from 'react';

export type Tab = 'home' | 'tasks' | 'performance' | 'settings';
export type Route = { tab: Tab; leadId: string | null };

export function parseRoute(path: string, search = ''): Route {
  const lead = /^\/contact\/([^/?#]+)/.exec(path);
  if (lead) return { tab: 'home', leadId: decodeURIComponent(lead[1]) };
  const legacy = new URLSearchParams(search).get('lead'); // notifications sent before the addresses existed: /?lead=<id>
  if (legacy) return { tab: 'home', leadId: legacy };
  if (new URLSearchParams(search).has('installed')) return { tab: 'settings', leadId: null }; // back from the CRM install
  if (/^\/stats\/?$/.test(path)) return { tab: 'performance', leadId: null };
  if (/^\/tasks\/?$/.test(path)) return { tab: 'tasks', leadId: null };
  if (/^\/settings\/?$/.test(path)) return { tab: 'settings', leadId: null };
  return { tab: 'home', leadId: null };
}

export function routePath(r: Route): string {
  if (r.leadId) return `/contact/${encodeURIComponent(r.leadId)}`;
  return r.tab === 'performance' ? '/stats' : r.tab === 'settings' ? '/settings' : r.tab === 'tasks' ? '/tasks' : '/';
}

/** Marks history entries this app pushed for an opened lead: closing it goes back, rather than adding an entry. */
const OPENED = 'piperOpened';

export function useRoute() {
  const [route, setRoute] = useState<Route>(() => parseRoute(location.pathname, location.search));
  useEffect(() => {
    // tidy legacy / unknown addresses into canonical ones, keeping the entry
    const canonical = routePath(route);
    if (location.pathname + location.search !== canonical) history.replaceState(history.state, '', canonical);
    const onPop = () => setRoute(parseRoute(location.pathname, location.search));
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const go = useCallback((r: Route, opts: { replace?: boolean; opened?: boolean } = {}) => {
    const path = routePath(r);
    if (path !== location.pathname) {
      const state = opts.opened ? { [OPENED]: true } : null;
      if (opts.replace) history.replaceState(state, '', path); else history.pushState(state, '', path);
    }
    setRoute(r);
  }, []);

  const setTab = useCallback((tab: Tab) => go({ tab, leadId: null }), [go]);
  const openLead = useCallback((id: string) => {
    // one lead opened from another swaps the entry, so back still lands on the board
    const opened = !!(history.state as Record<string, unknown> | null)?.[OPENED];
    go({ tab: 'home', leadId: id }, { replace: opened, opened: true });
  }, [go]);
  const closeLead = useCallback(() => {
    if ((history.state as Record<string, unknown> | null)?.[OPENED]) history.back(); // popstate updates the route
    else go({ tab: 'home', leadId: null }, { replace: true }); // opened by address (a link, a notification, a reload)
  }, [go]);
  /** Follow an address inside the app (e.g. a tapped notification while the app is open). */
  const follow = useCallback((url: string) => {
    const u = new URL(url, location.origin);
    const r = parseRoute(u.pathname, u.search);
    if (r.leadId) openLead(r.leadId); else go(r);
  }, [go, openLead]);

  return { route, setTab, openLead, closeLead, follow };
}
